import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast, Toaster } from "sonner";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Heart, Eye, EyeOff, Users, Star, BookOpen, Palette, DollarSign, Crown, Zap, Upload, X, Info, Check, ArrowLeft, ArrowRight, Move, GripVertical, Eye as EyeIcon, Lock, Clock, Paintbrush } from "lucide-react";
import axios from "axios";
import { LOGO_SRC, LOGO_ALT, BRAND_NAME, BRAND_TAGLINE } from "./config/brand";
import Navigation from "./components/Navigation";
import NewNavigation from "./components/NewNavigation";
import CookieConsent from "./components/CookieConsent";
import GlobalBackButton from "./components/GlobalBackButton";
import Footer from "./components/Footer";
import AnnouncementBar from "./components/AnnouncementBar";
import OnboardingTutorial from "./components/OnboardingTutorial";
import OnboardingGenreModal from "./components/OnboardingGenreModal";
import EarlyAccessGate from "./components/EarlyAccessGate";
import PageRegistryGuard from "./components/PageRegistryGuard";
import CanonicalRouteProvider from "./components/CanonicalRouteProvider";
import PageRenderer from "./components/PageRenderer";
import analytics from "./services/analytics";
import ComicsPage from "./pages/ComicsPage";
import NovelsPage from "./pages/NovelsPage";
// import MusicPage from "./pages/MusicPage"; // legacy music page (disabled)
import LivePage from "./pages/LivePage";
import CommunityPage from "./pages/CommunityPage";
import MaturePage from "./pages/MaturePage";
import Homepage from "./pages/Homepage";
import LandingPage from "./pages/LandingPage";
import EarlyAccessPage from "./pages/EarlyAccessPage";
import SeriesDetail from "./pages/SeriesDetail"; // Legacy - kept for admin debug mode only
import { SeriesPageRenderer } from "./components/page-builder/series"; // Canonical renderer
import { NovelPageRenderer } from "./components/page-builder/novel"; // Canonical renderer
import { CreatorPageRenderer } from "./components/page-builder/creator"; // Canonical renderer
import { ReaderPageRenderer } from "./components/page-builder/reader"; // Canonical reader
import EpisodeReader from "./pages/EpisodeReader";
import EpisodePageManager from "./pages/EpisodePageManager";
import NovelReader from "./pages/NovelReader";
import ReaderDashboard from "./pages/ReaderDashboard";
import CreatorDashboard from "./pages/CreatorDashboard";
import CreatorPublicProfile from "./pages/CreatorPublicProfile";
import EditProfile from "./pages/EditProfile";
import ProfileEditor from "./pages/ProfileEditor";
import ProfileRedirect from "./pages/ProfileRedirect";
import UnifiedProfile from "./pages/UnifiedProfile";
import CreatorProfile from "./pages/CreatorProfile";
import { CreatorRedirect, ReaderRedirect, UserRedirect, AtHandleRedirect } from "./pages/ProfileRedirects";
import UploadArtwork from "./pages/UploadArtwork";
import ArtGallery from "./pages/ArtGallery";
import ArtworkDetail from "./pages/ArtworkDetail";
import AuthCallback from "./pages/AuthCallback";
import PublishHub from "./pages/PublishHub";
import CreateNovel from "./pages/CreateNovel";
import AddChapter from "./pages/AddChapter";
import NovelDashboard from "./pages/NovelDashboard";
import CreatorOnboarding from "./components/CreatorOnboarding";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DMCAPolicy from "./pages/DMCAPolicy";
import ContentGuidelines from "./pages/ContentGuidelines";
import Feed from "./pages/Feed";
import Leaderboard from "./pages/Leaderboard";
import CreatorAnalytics from "./pages/CreatorAnalytics";
import EmailTesting from "./pages/EmailTesting";
import BulkUpload from "./pages/BulkUpload";
import TipSuccess from "./pages/TipSuccess";
import FollowList from "./pages/FollowList";
import AdminDashboard from "./pages/AdminDashboard";
import SiteManager from "./pages/SiteManager";
import { AdminUsersList, AdminCreatorsList, AdminSeriesList, AdminArtworksList, AdminReportsList, AdminNovelsList, AdminAuditLogs } from "./pages/AdminListPages";
import { AdminUserDetail, AdminSeriesDetail, AdminArtworkDetail, AdminReportDetail, AdminNovelDetail } from "./pages/AdminDetailPages";
import AdminCoverAudit from "./pages/admin/AdminCoverAudit";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminCreatorActivation from "./pages/admin/AdminCreatorActivation";
import AdminOnboardingMetrics from "./pages/admin/AdminOnboardingMetrics";
import PageBuilder from "./pages/admin/PageBuilder";
import PageBuilderIndex from "./pages/admin/PageBuilderIndex";
import PageBuilderIndexRenderer from "./pages/admin/PageBuilderIndexRenderer";
import PageBuilderStudio from "./components/PageBuilderStudio";
import { DesignTokensProvider } from "./components/page-builder";
import AdminSiteConfig from "./pages/admin/AdminSiteConfig";
import AdminAlertSettings from "./pages/admin/AdminAlertSettings";
import AdminPublishers from "./pages/admin/AdminPublishers";
import AdminPublisherAssociations from "./pages/admin/AdminPublisherAssociations";
import AdminPublisherRequests from "./pages/admin/AdminPublisherRequests";
import AdminHomepageHealth from "./pages/admin/AdminHomepageHealth";
import AdminBugReports from "./pages/admin/AdminBugReports";
import BugReportWidget from "./components/BugReportWidget";
import PublisherPage from "./pages/PublisherPage";
import PublishersPage from "./pages/PublishersPage";
import VerificationSubmission from "./pages/VerificationSubmission";
import HumanVerifiedLanding from "./pages/HumanVerifiedLanding";
import Messages from "./pages/Messages";
import SavedLibrary from "./pages/SavedLibrary";
import UserInbox from "./pages/UserInbox";
import UserRewards from "./pages/UserRewards";
import CollabBoard from "./pages/CollabBoard";
import CollabRequestDetail from "./pages/CollabRequestDetail";
import ResetPassword from "./pages/ResetPassword";
import SearchResults from "./pages/SearchResults";
import { ChatPopupProvider } from "./contexts/ChatPopupContext";
import { SiteConfigProvider } from "./contexts/SiteConfigContext";
import ChatPopup from "./components/ChatPopup";
import { BACKEND_URL, API_URL } from "./config/api";

const API = API_URL;

// Build version stamp — logged once at app startup for deploy verification
/* eslint-disable no-undef */
const _BUILD_ID = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';
const _BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'unknown';
/* eslint-enable no-undef */
console.info(
  `%c[Trap Comix] Build ${_BUILD_ID} (${_BUILD_TIME})`,
  'color: #f97316; font-weight: bold; font-size: 12px;'
);

// Feature Flags
const USE_CANONICAL_CREATOR_PROFILE = false; // Set to true to use PageBuilder renderer for creator profiles
const USE_CANONICAL_SERIES_DETAIL = true;    // Set to false to revert to legacy SeriesDetail
const USE_CANONICAL_NOVEL_DETAIL = true;     // Set to false to revert to legacy NovelDetail

// Fallback when canonical novel renderer is disabled (no legacy component exists)
const NovelDisabledFallback = () => (
  <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="novel-disabled-fallback">
    <div className="max-w-md text-center">
      <BookOpen className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white mb-2">Novel Page Unavailable</h1>
      <p className="text-zinc-400 mb-6">This novel page is temporarily unavailable. Please browse our novel catalog instead.</p>
      <Link to="/novels">
        <Button className="bg-orange-500 hover:bg-orange-600">Browse Novels</Button>
      </Link>
    </div>
  </div>
);

// Auth Context - Using React Context for proper state management
export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    // Initialize auth state on app load
    const initAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Refresh profile_image_url from server after initial load
  useEffect(() => {
    if (!token || !user) return;
    const ctrl = new AbortController();
    axios.get('/api/users/me', {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
    }).then(res => {
      const pimg = res.data?.profile_image_url;
      if (pimg !== user.profile_image_url) {
        const updated = { ...user, profile_image_url: pimg || null };
        setUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
      }
    }).catch(() => {});
    return () => ctrl.abort();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user  // Add isAuthenticated computed property
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hero Section Component
const HeroSection = () => {
  const { user } = useAuth();
  
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1530406831759-15c5c0cbce8b?w=1920&h=1080&fit=crop')`
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 max-w-4xl">
        <Badge className="mb-6 bg-orange-500 hover:bg-orange-600 text-white text-lg px-6 py-2">
          <Crown className="w-5 h-5 mr-2" />
          Welcome to the Block Party
        </Badge>
        
        <img 
          src={LOGO_SRC} 
          alt={LOGO_ALT} 
          className="w-96 mx-auto mb-6"
        />
        
        <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto font-medium">
          Where Black, Brown, and indie creators own their stories. 
          <br />
          <span className="text-orange-400 font-bold">Fair revenue. Real community. Your IP.</span>
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/browse">
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-8 py-6 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300">
              <BookOpen className="w-6 h-6 mr-2" />
              Read Comics
            </Button>
          </Link>
          
          {user && (user.role === 'artist' || user.role === 'author') ? (
            <Link to="/create">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-6 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300">
                <Palette className="w-6 h-6 mr-2" />
                Create Your Series
              </Button>
            </Link>
          ) : (
            <Link to="/auth">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-6 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300">
                <Palette className="w-6 h-6 mr-2" />
                Join as Creator
              </Button>
            </Link>
          )}
        </div>
        
        <div className="mt-12 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-400">100%</div>
            <div className="text-sm text-gray-300">IP Ownership</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-400">Fair</div>
            <div className="text-sm text-gray-300">Revenue Split</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-400">Real</div>
            <div className="text-sm text-gray-300">Community</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Browse Comics Component
const BrowseComics = () => {
  const [featuredContent, setFeaturedContent] = useState({ featured_series: [], trending_creators: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedContent();
  }, []);

  const fetchFeaturedContent = async () => {
    try {
      const response = await axios.get(`${API}/featured`);
      setFeaturedContent(response.data);
    } catch (error) {
      console.error('Error fetching featured content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-2xl font-bold">Loading amazing comics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black py-16">
        <div className="container mx-auto px-6">
          <h1 className="text-5xl font-black mb-4">
            Discover <span className="text-orange-400">Amazing</span> Comics
          </h1>
          <p className="text-xl text-gray-300">From underground legends to rising stars</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {/* Featured Series */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 flex items-center">
            <Zap className="w-8 h-8 mr-3 text-orange-400" />
            Featured Series
          </h2>
          
          {featuredContent.featured_series.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredContent.featured_series.map((series) => (
                <SeriesCard key={series.id} series={series} />
              ))}
            </div>
          ) : (
            <Card className="bg-gray-900 border-gray-700 text-center py-12">
              <CardContent>
                <p className="text-gray-400 text-lg">No series available yet. Be the first to create one!</p>
                <Link to="/create">
                  <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                    Create Your Series
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Trending Creators */}
        <section>
          <h2 className="text-3xl font-bold mb-8 flex items-center">
            <Star className="w-8 h-8 mr-3 text-orange-400" />
            Trending Creators
          </h2>
          
          {featuredContent.trending_creators.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredContent.trending_creators.map((creator) => (
                <CreatorCard key={creator.id} creator={creator} />
              ))}
            </div>
          ) : (
            <Card className="bg-gray-900 border-gray-700 text-center py-12">
              <CardContent>
                <p className="text-gray-400 text-lg">Join our community of creators!</p>
                <Link to="/auth">
                  <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                    Join Trap Comix
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};

// Series Card Component
const SeriesCard = ({ series, showUploadButton = false }) => {
  const { user } = useAuth();
  
  return (
    <Card className="bg-gray-900 border-gray-700 hover:border-orange-500 transition-all duration-300 group cursor-pointer">
      <div className="aspect-[2/3] bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-lg relative overflow-hidden">
        {series.cover_url || series.cover_image_url ? (
          <img 
            src={series.cover_url || series.cover_image_url} 
            alt={series.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <img 
              src="https://images.unsplash.com/photo-1601645191163-3fc0d5d64e35?w=400&h=600&fit=crop" 
              alt="Default comic cover"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white font-bold text-lg mb-1">{series.title}</h3>
          <p className="text-gray-300 text-sm">
            {series.metrics_cache?.chapters || series.chapters_count || 0} episodes
          </p>
        </div>
      </div>
      
      <CardContent className="p-4">
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{series.description}</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {(series.genres || series.tags || []).slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs bg-gray-800 text-gray-300">
              {tag}
            </Badge>
          ))}
        </div>
        
        <div className="space-y-2">
          <Link to={`/series/${series.id}`}>
            <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              Read Series
            </Button>
          </Link>
          
          {showUploadButton && user && (
            <Link to={`/series/${series.id}/upload`}>
              <Button variant="outline" className="w-full border-green-500 text-green-400 hover:bg-green-500 hover:text-white">
                <Upload className="w-4 h-4 mr-2" />
                Upload Episode
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Creator Card Component
const CreatorCard = ({ creator }) => {
  const displayName = creator.display_name || creator.handle || 'Creator';
  const isFoundingMember = creator.founder_flags?.is_founding_member;
  const followerCount = creator.metrics_cache?.followers || 0;
  
  return (
    <Card className="bg-gray-900 border-gray-700 hover:border-orange-500 transition-all duration-300 text-center">
      <CardContent className="p-6">
        <Avatar className="w-20 h-20 mx-auto mb-4 border-2 border-orange-400">
          <AvatarImage src={creator.avatar_url} alt={displayName} />
          <AvatarFallback className="bg-orange-500 text-white text-xl font-bold">
            {displayName.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        <h3 className="text-white font-bold text-lg mb-1 flex items-center justify-center">
          {displayName}
          {isFoundingMember && (
            <Crown className="w-4 h-4 ml-1 text-orange-400" />
          )}
        </h3>
        
        <Badge className="mb-3 bg-gray-800 text-gray-300 capitalize">
          @{creator.handle}
        </Badge>
        
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
          {creator.bio_richtext || "Creating amazing content on Trap Comix"}
        </p>
        
        <div className="flex justify-center space-x-4 text-sm text-gray-400 mb-4">
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            {followerCount}
          </div>
        </div>
        
        <Link to={`/creator/${creator.handle}`}>
          <Button variant="outline" className="w-full border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white">
            View Profile
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

// Webtoon-Style Comic Reader for Trap Comix
const ComicReader = () => {
  const { seriesId, chapterId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [viewMode, setViewMode] = useState('vertical');
  const { user } = useAuth();

  // Demo pages for testing (will be replaced with actual chapter pages)
  const demoPages = [
    'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=800&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1618336753974-aae8e04506aa?w=800&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1609743522471-83c84ce23e32?w=800&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=800&h=1200&fit=crop',
  ];

  // Touch handling for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    if (seriesId && chapterId) {
      fetchSeriesData();
    }
  }, [seriesId, chapterId]);

  // Detect device type for default view mode
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setViewMode(isMobile ? 'vertical' : 'vertical'); // Always default to vertical
  }, []);

  const fetchSeriesData = async () => {
    try {
      const [seriesResponse, chaptersResponse] = await Promise.all([
        axios.get(`${API}/series/${seriesId}`),
        axios.get(`${API}/series/${seriesId}/chapters`)
      ]);
      
      setSeries(seriesResponse.data);
      setChapters(chaptersResponse.data);
      
      // Find and set the current chapter based on chapterId
      const chapter = chaptersResponse.data.find(ch => ch.id === chapterId);
      if (chapter) {
        setCurrentChapter(chapter);
      } else if (chaptersResponse.data.length > 0) {
        // Fallback to first chapter if chapterId not found
        setCurrentChapter(chaptersResponse.data[0]);
      }
      
      if (chaptersResponse.data.length > 0) {
        setCurrentChapter(chaptersResponse.data[0]);
      }
    } catch (error) {
      console.error('Error fetching series data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierType) => {
    if (!user) {
      toast.error("Please login to subscribe");
      return;
    }

    try {
      const response = await axios.post(`${API}/payments/subscribe`, null, {
        params: {
          tier_id: tierType,
          creator_id: series.creator_id
        }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error("Failed to create subscription");
    }
  };

  const handleTip = async (tipType) => {
    if (!user) {
      toast.error("Please login to tip");
      return;
    }

    try {
      const response = await axios.post(`${API}/payments/tip`, null, {
        params: {
          tip_id: tipType,
          creator_id: series.creator_id
        }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating tip:', error);
      toast.error("Failed to create tip");
    }
  };

  const nextPage = () => {
    if (currentPage < demoPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter.id);
    if (currentIndex < chapters.length - 1) {
      const nextChap = chapters[currentIndex + 1];
      navigate(`/series/${seriesId}/read/${nextChap.id}`);
    }
  };

  const prevChapter = () => {
    const currentIndex = chapters.findIndex(ch => ch.id === currentChapter.id);
    if (currentIndex > 0) {
      const prevChap = chapters[currentIndex - 1];
      navigate(`/series/${seriesId}/read/${prevChap.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading your comic...</div>
        </div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Series not found</h2>
          <Link to="/browse">
            <Button className="bg-orange-500 hover:bg-orange-600">
              Browse Comics
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Minimal Header - Only visible on desktop */}
      <div className="hidden md:block sticky top-0 z-40 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to={`/series/${seriesId}`}>
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  ← Back to Series
                </Button>
              </Link>
              <div>
                <h1 className="text-lg font-bold">{series.title}</h1>
                <p className="text-sm text-gray-400">Chapter {currentChapter?.chapter_number}</p>
              </div>
            </div>
            
            {/* View Mode Toggle - Desktop Only */}
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'vertical' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('vertical')}
                className="text-xs"
              >
                Webtoon
              </Button>
              <Button
                variant={viewMode === 'horizontal' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('horizontal')}
                className="text-xs"
              >
                Pages
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Support Button - Always visible */}
      <div className="fixed bottom-6 right-6 z-50">
        <Dialog open={showSupportModal} onOpenChange={setShowSupportModal}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-6 py-3 rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300">
              <Heart className="w-5 h-5 mr-2" />
              Support
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Support this Creator</DialogTitle>
              <DialogDescription className="text-gray-400">
                Choose how you’d like to support {series.title}
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="subscribe" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-800">
                <TabsTrigger value="subscribe">Subscribe</TabsTrigger>
                <TabsTrigger value="tip">One-time Tip</TabsTrigger>
              </TabsList>
              
              <TabsContent value="subscribe" className="space-y-3 mt-4">
                <Button 
                  onClick={() => handleSubscribe('fan')} 
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 p-4 h-auto justify-between"
                >
                  <div className="text-left">
                    <div className="font-bold">Fan Tier</div>
                    <div className="text-sm opacity-80">Support your favorite creators</div>
                  </div>
                  <div className="text-xl font-bold">$3/month</div>
                </Button>
                
                <Button 
                  onClick={() => handleSubscribe('supporter')} 
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 p-4 h-auto justify-between"
                >
                  <div className="text-left">
                    <div className="font-bold">Supporter Tier</div>
                    <div className="text-sm opacity-80">Get exclusive content</div>
                  </div>
                  <div className="text-xl font-bold">$7/month</div>
                </Button>
                
                <Button 
                  onClick={() => handleSubscribe('patron')} 
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 p-4 h-auto justify-between"
                >
                  <div className="text-left">
                    <div className="font-bold">Patron Tier</div>
                    <div className="text-sm opacity-80">Premium access and perks</div>
                  </div>
                  <div className="text-xl font-bold">$15/month</div>
                </Button>
              </TabsContent>
              
              <TabsContent value="tip" className="space-y-3 mt-4">
                <Button 
                  onClick={() => handleTip('small')} 
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 p-3 h-auto justify-between"
                >
                  <span className="font-bold">Small Tip</span>
                  <span className="text-xl font-bold">$2</span>
                </Button>
                
                <Button 
                  onClick={() => handleTip('medium')} 
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 p-3 h-auto justify-between"
                >
                  <span className="font-bold">Medium Tip</span>
                  <span className="text-xl font-bold">$5</span>
                </Button>
                
                <Button 
                  onClick={() => handleTip('large')} 
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 p-3 h-auto justify-between"
                >
                  <span className="font-bold">Large Tip</span>
                  <span className="text-xl font-bold">$10</span>
                </Button>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Comic Reader Content */}
      {currentChapter ? (
        <div className="relative">
          {/* Mobile Chapter Info */}
          <div className="md:hidden text-center py-4 px-6 bg-gray-900 sticky top-0 z-30">
            <h2 className="text-lg font-bold mb-1">{currentChapter.title}</h2>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
              <span>Chapter {currentChapter.chapter_number}</span>
              <span className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                {currentChapter.views_count} views
              </span>
            </div>
          </div>

          {/* Vertical Reading Mode (Webtoon Style) */}
          {viewMode === 'vertical' && (
            <div className="max-w-2xl mx-auto">
              <div className="space-y-1">
                {demoPages.map((pageUrl, index) => (
                  <div key={index} className="w-full">
                    <img 
                      src={pageUrl} 
                      alt={`Page ${index + 1}`}
                      className="w-full h-auto block"
                      loading="lazy"
                      style={{ 
                        imageRendering: 'crisp-edges',
                        maxWidth: '100%',
                        height: 'auto'
                      }}
                    />
                  </div>
                ))}
              </div>
              
              {/* Chapter Navigation - Vertical Mode */}
              <div className="py-8 px-6">
                <div className="flex justify-between items-center max-w-md mx-auto">
                  <Button 
                    onClick={prevChapter}
                    disabled={chapters.findIndex(ch => ch.id === currentChapter.id) === 0}
                    variant="outline" 
                    className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                  >
                    ← Previous
                  </Button>
                  
                  <div className="text-center text-gray-400 text-sm">
                    <div>Chapter {currentChapter.chapter_number}</div>
                    <div>{chapters.length} total</div>
                  </div>
                  
                  <Button 
                    onClick={nextChapter}
                    disabled={chapters.findIndex(ch => ch.id === currentChapter.id) === chapters.length - 1}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Horizontal Reading Mode (Traditional Pages) */}
          {viewMode === 'horizontal' && (
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="relative max-w-4xl w-full">
                {/* Current Page */}
                <div className="text-center">
                  <img 
                    src={demoPages[currentPage]} 
                    alt={`Page ${currentPage + 1}`}
                    className="max-w-full max-h-[80vh] mx-auto rounded-lg shadow-2xl"
                    loading="lazy"
                  />
                </div>
                
                {/* Navigation Controls */}
                <div className="absolute inset-y-0 left-0 flex items-center">
                  <Button
                    onClick={prevPage}
                    disabled={currentPage === 0}
                    variant="ghost"
                    size="lg"
                    className="text-white hover:bg-black/50 disabled:opacity-50 h-16 w-16 rounded-full"
                  >
                    ←
                  </Button>
                </div>
                
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <Button
                    onClick={nextPage}
                    disabled={currentPage === demoPages.length - 1}
                    variant="ghost"
                    size="lg"
                    className="text-white hover:bg-black/50 disabled:opacity-50 h-16 w-16 rounded-full"
                  >
                    →
                  </Button>
                </div>
                
                {/* Page Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
                  {currentPage + 1} / {demoPages.length}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No chapters available yet.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Auth Component - Unified Profile with Emergent Auth (Google OAuth)
const AuthPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // Initialize mode from URL params (supports ?mode=signup)
  const getInitialMode = () => {
    const params = new URLSearchParams(location.search);
    const urlMode = params.get('mode');
    if (urlMode === 'signup' || urlMode === 'register') return 'signup';
    if (urlMode === 'forgot') return 'forgot';
    return 'login';
  };
  
  const [mode, setMode] = useState(getInitialMode); // 'login', 'signup', or 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'basic_user' // Default role for all new users
  });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Update mode when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlMode = params.get('mode');
    if (urlMode === 'signup' || urlMode === 'register') setMode('signup');
    else if (urlMode === 'forgot') setMode('forgot');
  }, [location.search]);

  // Detect Caps Lock (with fallback heuristic)
  const detectCapsLock = (event) => {
    if (event.getModifierState && typeof event.getModifierState === 'function') {
      return event.getModifierState('CapsLock');
    }

    const key = event.key;
    if (!key || key.length !== 1) return capsLockOn;

    const isLetter = key.toLowerCase() !== key.toUpperCase();
    if (!isLetter) return capsLockOn;

    const isUpper = key === key.toUpperCase();
    const isShift = event.shiftKey;

    if (isUpper && !isShift) return true;
    if (!isUpper && isShift) return true;
    return false;
  };

  const handleKeyDown = (e) => {
    setCapsLockOn(detectCapsLock(e));
  };

  const handleKeyUp = (e) => {
    setCapsLockOn(detectCapsLock(e));
  };

  useEffect(() => {
    // Check if there's a session_id in the URL hash (from Emergent Auth redirect)
    const processSessionId = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setLoading(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          // Call our backend to process the Emergent session
          const response = await axios.post(`${API}/auth/session`, {}, {
            headers: {
              'X-Session-ID': sessionId
            }
          });
          
          // Store the Emergent session token in httpOnly cookie (simulated with localStorage for now)
          if (response.data.emergent_session_token) {
            localStorage.setItem('emergent_session_token', response.data.emergent_session_token);
          }
          
          // Login with our app token
          login(response.data.user, response.data.access_token);
          
          // Clean the URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Welcome message
          const isCreator = response.data.profile?.is_creator;
          toast.success(isCreator ? 'Welcome back to Creator Studio! 🎨' : 'Welcome to Trap Comix! 📚');
          
          // Clear loading before navigation
          setLoading(false);
          
          // Redirect to dashboard
          navigate('/dashboard');
        } catch (error) {
          console.error('Session processing error:', error);
          let errorMsg = 'Authentication failed. Please try again.';
          
          if (error.response?.data?.detail) {
            errorMsg = error.response.data.detail;
          }
          
          toast.error(errorMsg);
          setLoading(false);
          
          // Clean the URL even on failure
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    processSessionId();
  }, []);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      // Login with token
      login(response.data.user, response.data.access_token);
      
      // Welcome message
      toast.success(mode === 'login' ? 'Welcome back! 🎉' : 'Account created! Welcome to Trap Comix! 🎨');
      
      // Clear loading before navigation to prevent hanging state
      setLoading(false);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      let errorMsg = 'Authentication failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Open OAuth in a popup window
    const redirectUrl = encodeURIComponent(`${window.location.origin}/auth/callback`);
    const authUrl = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
    
    // Popup dimensions
    const width = 500;
    const height = 600;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    // Open popup
    const popup = window.open(
      authUrl,
      'Google Sign In',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    // Listen for message from popup
    window.addEventListener('message', handleOAuthCallback);
    
    // Check if popup was closed
    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        window.removeEventListener('message', handleOAuthCallback);
      }
    }, 1000);
  };

  const handleOAuthCallback = async (event) => {
    // Verify origin for security
    if (event.origin !== window.location.origin) return;
    
    if (event.data.type === 'oauth_success' && event.data.session_id) {
      setLoading(true);
      
      try {
        // Process the session
        const response = await axios.post(`${API}/auth/session`, {}, {
          headers: {
            'X-Session-ID': event.data.session_id
          }
        });
        
        // Store tokens
        if (response.data.emergent_session_token) {
          localStorage.setItem('emergent_session_token', response.data.emergent_session_token);
        }
        
        // Login
        login(response.data.user, response.data.access_token);
        
        // Welcome message
        const isCreator = response.data.profile?.is_creator;
        toast.success(isCreator ? 'Welcome back to Creator Studio! 🎨' : 'Welcome to Trap Comix! 📚');
        
        // Clear loading before navigation
        setLoading(false);
        
        // Redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        console.error('Session processing error:', error);
        let errorMsg = 'Authentication failed. Please try again.';
        
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.code === 'ERR_NETWORK') {
          errorMsg = 'Network error. Please try again.';
        }
        
        toast.error(errorMsg);
        setLoading(false);
      }
    }
  };

  // Handle Forgot Password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail || !forgotEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMsg = error.response?.data?.detail || 'Unable to process request. Please try again later.';
      // Don't reveal if email exists or not for security
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait before trying again.');
      } else {
        // Always show success-like message for security
        setForgotSuccess(true);
        toast.success('If an account exists for this email, we sent a reset link.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Signing you in...</p>
        </div>
      </div>
    );
  }

  // Forgot Password Mode
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src={LOGO_SRC} 
              alt={LOGO_ALT} 
              className="w-64 mx-auto mb-4"
            />
          </div>

          <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-gray-700 shadow-2xl relative z-50">
            <CardHeader className="text-center pb-4">
              <h2 className="text-2xl font-bold text-white mb-2">Reset Your Password</h2>
              <p className="text-gray-400">Enter your email to receive a reset link</p>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {forgotSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Check Your Email</h3>
                  <p className="text-gray-400 text-sm mb-6">
                    If an account exists for <span className="text-orange-400">{forgotEmail}</span>, we sent a password reset link.
                  </p>
                  <Button 
                    onClick={() => {
                      setMode('login');
                      setForgotSuccess(false);
                      setForgotEmail('');
                    }}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-4 rounded-xl"
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              )}

              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setForgotSuccess(false);
                    setForgotEmail('');
                  }}
                  className="text-sm text-orange-400 hover:text-orange-300 font-medium"
                >
                  ← Back to Sign In
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-start justify-center px-6 pt-8 pb-48">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src={LOGO_SRC} 
            alt={LOGO_ALT} 
            className="w-64 mx-auto mb-4"
          />
          <p className="text-gray-400 text-lg">Where creators own their stories</p>
        </div>

        <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-gray-700 shadow-2xl relative z-50">
          <CardHeader className="text-center pb-4">
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to the Block Party</h2>
            <p className="text-gray-400">{mode === 'login' ? 'Sign in to continue' : 'Create your account'}</p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Email/Password Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none focus:text-orange-400 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Caps Lock Warning */}
                {capsLockOn && (
                  <p className="mt-1 text-xs text-yellow-400 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Caps Lock is on
                  </p>
                )}
                {/* Forgot Password Link (login mode only) */}
                {mode === 'login' && (
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-sm text-orange-400 hover:text-orange-300 font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              <Button 
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-900 text-gray-400">OR</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <Button 
              onClick={handleGoogleSignIn}
              type="button"
              className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {/* Toggle between login/signup */}
            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                className="text-sm text-orange-400 hover:text-orange-300 font-medium"
              >
                {mode === 'login' ? "Don't have an account? Sign Up" : 'Already have an account? Sign In'}
              </button>
            </div>

            <p className="text-xs text-center text-gray-500 pt-2">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-sm mt-6">
          Email/Password or Google OAuth
        </p>
      </div>
    </div>
  );
};

// Duplicate CreatorProfile component removed - using imported version from ./pages/CreatorProfile

// Create Series Component (Webtoon-style form)
const CreateSeries = () => {
  const { user, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    category1: '',
    category2: '',
    readingFormat: 'vertical', // 'vertical' | 'horizontal_scroll' | 'paged'
    contentRating: {
      violence: '',
      nudity: '',
      sexual: '',
      profanity: '',
      substances: '',
      sensitive: ''
    }
  });
  
  const [thumbnails, setThumbnails] = useState({
    square: { file: null, preview: null },
    vertical: { file: null, preview: null },
    billboard: { file: null, preview: null }
  });
  
  const [agreedToRating, setAgreedToRating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState({ square: false, vertical: false, billboard: false });

  // Categories for dropdowns
  const categories = [
    "Action", "Adventure", "Comedy", "Drama", "Fantasy", "Horror", 
    "Romance", "Sci-Fi", "Slice of Life", "Thriller", "Mystery", 
    "Historical", "Sports", "Supernatural", "Urban", "Music"
  ];

  // Content rating options
  const contentOptions = [
    "None", "Mild", "Moderate", "Strong", "Extreme"
  ];

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      toast.error("Please login to create a series");
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContentRatingChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      contentRating: {
        ...prev.contentRating,
        [type]: value
      }
    }));
  };

  const handleFileUpload = (type, file) => {
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error("Only JPG and PNG files are allowed");
      return;
    }

    // Validate file size (slightly larger allowance for billboard)
    const maxSize = type === 'square' ? 500 * 1024 : type === 'vertical' ? 700 * 1024 : 900 * 1024;
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${Math.round(maxSize / 1024)}KB`);
      return;
    }

    // Calculate aspect ratio and warn if off
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      let targetRatio = null;
      let label = '';

      if (type === 'vertical') {
        targetRatio = 2 / 3; // portrait cover
        label = '2:3 portrait';
      } else if (type === 'billboard') {
        targetRatio = 16 / 9; // wide hero
        label = '16:9 wide';
      }

      if (targetRatio) {
        const lower = targetRatio * 0.9;
        const upper = targetRatio * 1.1;
        if (ratio < lower || ratio > upper) {
          const approx = (ratio).toFixed(2);
          toast.warning(`Recommended aspect is ${label}. Your image is ~${approx}:1, so it may be padded or cropped.`);
        }
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnails(prev => ({
          ...prev,
          [type]: {
            file: file,
            preview: e.target.result
          }
        }));
      };
      reader.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    setDragActive(prev => ({ ...prev, [type]: false }));
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(type, files[0]);
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    setDragActive(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    setDragActive(prev => ({ ...prev, [type]: false }));
  };

  const removeThumbnail = (type) => {
    setThumbnails(prev => ({
      ...prev,
      [type]: { file: null, preview: null }
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error("Series title is required");
      return false;
    }
    if (formData.title.length > 50) {
      toast.error("Series title must be 50 characters or less");
      return false;
    }
    if (!formData.summary.trim()) {
      toast.error("Series summary is required");
      return false;
    }
    if (formData.summary.length > 500) {
      toast.error("Summary must be 500 characters or less");
      return false;
    }
    if (!formData.category1) {
      toast.error("Category 1 is required");
      return false;
    }

    // Check all content ratings are selected
    const contentRatings = Object.values(formData.contentRating);
    if (contentRatings.some(rating => !rating)) {
      toast.error("Please complete the content rating self-assessment");
      return false;
    }

    if (!agreedToRating) {
      toast.error("Please acknowledge the content rating");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      // Create series data
      const seriesData = {
        title: formData.title.trim(),
        description: formData.summary.trim(),
        genres: [formData.category1, formData.category2].filter(Boolean),
        tags: [],
        // Series Cover (Portrait 2:3)
        cover_url: thumbnails.vertical.preview || thumbnails.square.preview,
        format: formData.readingFormat === 'vertical' || formData.readingFormat === 'horizontal_scroll'
          ? 'webtoon'
          : 'manga',
        // reading_mode controls default reader mode: 'vertical', 'horizontal_scroll', or 'paged'
        reading_mode: formData.readingFormat,
        status: "ongoing"
      };

      const response = await axios.post(`${API}/series`, seriesData);
      
      // Refresh user data in case role was upgraded
      try {
        const userResponse = await axios.get(`${API}/users/me`);
        if (userResponse.data.role !== user.role) {
          const updatedUser = { ...user, role: userResponse.data.role };
          updateUser(updatedUser);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
      
      toast.success("Series created! Now upload your first episode 🎨");
      // Redirect to upload episode page for this series
      navigate(`/series/${response.data.id}/upload`, { 
        state: { 
          seriesData: response.data,
          coverPreview: thumbnails.square.preview 
        } 
      });
      
    } catch (error) {
      console.error('Error creating series:', error);
      toast.error(error.response?.data?.detail || "Failed to create series");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black py-8 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="flex items-center space-x-4">
            <Link to="/browse">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                ← Back to Browse
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-black text-white mb-2">CREATE SERIES</h1>
              <p className="text-gray-400">You can create a series in just a few minutes! But, let’s give you some tips before you start.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Thumbnails Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Square Thumbnail */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">Square Thumbnail</h3>
                <Info className="w-4 h-4 text-gray-400" />
              </div>
              
              <div 
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                  dragActive.square 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                } ${thumbnails.square.preview ? 'border-green-500' : ''}`}
                onDrop={(e) => handleDrop(e, 'square')}
                onDragOver={(e) => handleDragOver(e, 'square')}
                onDragLeave={(e) => handleDragLeave(e, 'square')}
              >
                {thumbnails.square.preview ? (
                  <div className="relative">
                    <img 
                      src={thumbnails.square.preview} 
                      alt="Square thumbnail preview"
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/75 text-white"
                      onClick={() => removeThumbnail('square')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-gray-700 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Select an image to upload.</p>
                      <p className="text-gray-400 text-sm">Or drag the image file here.</p>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => handleFileUpload('square', e.target.files[0])}
                      className="sr-only"
                      id="square-upload"
                    />
                    <label
                      htmlFor="square-upload"
                      className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>
              
              <div className="text-xs text-gray-400 space-y-1">
                <p>Image size must be 1080x1080.</p>
                <p>Image must be less than 500KB.</p>
                <p>Only JPG, JPEG, and PNG formats are allowed.</p>
              </div>
            </div>

            {/* Vertical Thumbnail */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">Vertical Thumbnail</h3>
                <Info className="w-4 h-4 text-gray-400" />
              </div>
              
              <div 
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                  dragActive.vertical 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                } ${thumbnails.vertical.preview ? 'border-green-500' : ''}`}
                onDrop={(e) => handleDrop(e, 'vertical')}
                onDragOver={(e) => handleDragOver(e, 'vertical')}
                onDragLeave={(e) => handleDragLeave(e, 'vertical')}
              >
                {thumbnails.vertical.preview ? (
                  <div className="relative">
                    <img 
                      src={thumbnails.vertical.preview} 
                      alt="Vertical thumbnail preview"
                      className="w-full h-80 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/75 text-white"
                      onClick={() => removeThumbnail('vertical')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-gray-700 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Select an image to upload.</p>
                      <p className="text-gray-400 text-sm">Or drag the image file here.</p>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => handleFileUpload('vertical', e.target.files[0])}
                      className="sr-only"
                      id="vertical-upload"
                    />
                    <label
                      htmlFor="vertical-upload"
                      className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-400 space-y-1">
                <p>Recommended aspect ratio: 2:3 (portrait). Images will be padded if the ratio is off.</p>
              </div>
            </div>

            {/* Billboard Thumbnail (Wide 16:9) */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold">Series Billboard (Wide 16:9)</h3>
                <Info className="w-4 h-4 text-gray-400" />
              </div>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                  dragActive.billboard
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-gray-600 hover:border-gray-500'
                } ${thumbnails.billboard.preview ? 'border-green-500' : ''}`}
                onDrop={(e) => handleDrop(e, 'billboard')}
                onDragOver={(e) => handleDragOver(e, 'billboard')}
                onDragLeave={(e) => handleDragLeave(e, 'billboard')}
              >
                {thumbnails.billboard.preview ? (
                  <div className="relative">
                    <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                      <img
                        src={thumbnails.billboard.preview}
                        alt="Billboard preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/75 text-white"
                      onClick={() => removeThumbnail('billboard')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-full aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                      <Upload className="w-10 h-10 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Upload a wide hero image for homepage and featured slots.</p>
                      <p className="text-gray-400 text-sm">Optional, but recommended for best presentation.</p>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => handleFileUpload('billboard', e.target.files[0])}
                      className="sr-only"
                      id="billboard-upload"
                    />
                    <label
                      htmlFor="billboard-upload"
                      className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p>Recommended aspect ratio: 16:9. Images will be cropped to fill the hero frame.</p>
              </div>
            </div>
          </div>

          {/* Form Fields Section */}
          <div className="space-y-6">
            
            {/* Series Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-white font-medium">Series Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Less than 50 characters"
                maxLength={50}
                className="bg-gray-800 border-gray-600 text-white focus:border-orange-500"
              />
              <div className="text-xs text-gray-400">
                {formData.title.length}/50 characters
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <Label htmlFor="summary" className="text-white font-medium">Summary</Label>
              <Textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                placeholder="Less than 500 characters"
                maxLength={500}
                rows={4}
                className="bg-gray-800 border-gray-600 text-white focus:border-orange-500 resize-none"
              />
              <div className="text-xs text-gray-400">
                {formData.summary.length}/500 characters
              </div>
            </div>

            {/* Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Category 1 (Required) */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Category 1</Label>
                <select
                  value={formData.category1}
                  onChange={(e) => handleInputChange('category1', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Select</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Category 2 (Optional) */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Category 2 <span className="text-gray-400">(optional)</span></Label>
                <select
                  value={formData.category2}
                  onChange={(e) => handleInputChange('category2', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Select</option>
                  {categories.filter(cat => cat !== formData.category1).map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reading Format Selection */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Label className="text-white font-medium text-lg">Reading Format</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                  onClick={() => toast.info("Choose how readers will experience your comic. Vertical for continuous scrolling (Webtoon-style) or Horizontal for page-by-page reading.")}
                >
                  <Info className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-gray-400 text-sm">
                This determines how your episodes will be displayed in the reader. You can’t change this after creation.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Vertical Scroll */}
                <button
                  type="button"
                  onClick={() => handleInputChange('readingFormat', 'vertical')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formData.readingFormat === 'vertical'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-gray-700 bg-gray-900 hover:border-gray-600'
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className="text-4xl">📱</div>
                    <h4 className="text-white font-bold">Vertical Scroll</h4>
                    <p className="text-gray-400 text-sm">
                      Webtoon-style continuous scrolling. Best for mobile readers. Upload tall panel images.
                    </p>
                    {formData.readingFormat === 'vertical' && (
                      <Badge className="bg-orange-500 text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Selected
                      </Badge>
                    )}
                  </div>
                </button>

                {/* Horizontal Scroll */}
                <button
                  type="button"
                  onClick={() => handleInputChange('readingFormat', 'horizontal_scroll')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formData.readingFormat === 'horizontal_scroll'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-gray-700 bg-gray-900 hover:border-gray-600'
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className="text-4xl">📜</div>
                    <h4 className="text-white font-bold">Horizontal Scroll</h4>
                    <p className="text-gray-400 text-sm">
                      Scroll left-to-right like a side-scrolling comic. Great for wide panels and cinematic layouts.
                    </p>
                    {formData.readingFormat === 'horizontal_scroll' && (
                      <Badge className="bg-orange-500 text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Selected
                      </Badge>
                    )}
                  </div>
                </button>

                {/* Horizontal Page / Page-turn */}
                <button
                  type="button"
                  onClick={() => handleInputChange('readingFormat', 'paged')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formData.readingFormat === 'paged'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-gray-700 bg-gray-900 hover:border-gray-600'
                  }`}
                >
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className="text-4xl">📖</div>
                    <h4 className="text-white font-bold">Horizontal Page</h4>
                    <p className="text-gray-400 text-sm">
                      Traditional comic book page-by-page. Best for desktop. Upload standard comic pages.
                    </p>
                    {formData.readingFormat === 'paged' && (
                      <Badge className="bg-orange-500 text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Selected
                      </Badge>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Content Rating Self Assessment */}
          <div className="space-y-6 bg-gray-900 p-6 rounded-lg border border-gray-700">
            <div className="flex items-center space-x-2">
              <h3 className="text-xl font-bold text-white">CONTENT RATING SELF ASSESSMENT</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={() => toast.info("Content ratings help users discover appropriate content for their preferences.")}
              >
                <Info className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-gray-300 text-sm leading-relaxed">
              All series on TRAP COMIX must self-declare a Content Rating. With visible Content Ratings, we can help users discover content that 
              should be appropriate for their age group and align with their content preferences.
            </p>
            
            <p className="text-gray-300 text-sm">
              To ensure the appropriate Content Rating is applied to your series, please respond to the following questionnaire regarding the 
              content of your series. Please note that ratings are subject to change at TRAP COMIX’s sole discretion and without prior notice.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Violence and graphic content */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Violent and graphic content</Label>
                <select
                  value={formData.contentRating.violence}
                  onChange={(e) => handleContentRatingChange('violence', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Nudity */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Nudity</Label>
                <select
                  value={formData.contentRating.nudity}
                  onChange={(e) => handleContentRatingChange('nudity', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Sexual content */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Sexual content</Label>
                <select
                  value={formData.contentRating.sexual}
                  onChange={(e) => handleContentRatingChange('sexual', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Profanity */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Profanity</Label>
                <select
                  value={formData.contentRating.profanity}
                  onChange={(e) => handleContentRatingChange('profanity', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Alcohol, drugs or tobacco */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Alcohol, drugs or tobacco</Label>
                <select
                  value={formData.contentRating.substances}
                  onChange={(e) => handleContentRatingChange('substances', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Sensitive themes and topics */}
              <div className="space-y-2">
                <Label className="text-white font-medium">Sensitive themes and topics</Label>
                <select
                  value={formData.contentRating.sensitive}
                  onChange={(e) => handleContentRatingChange('sensitive', e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Please select one</option>
                  {contentOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Legal Checkbox */}
            <div className="flex items-start space-x-3 mt-6">
              <input
                type="checkbox"
                id="content-rating-agreement"
                checked={agreedToRating}
                onChange={(e) => setAgreedToRating(e.target.checked)}
                className="mt-1 w-4 h-4 text-orange-500 bg-gray-800 border-gray-600 rounded focus:ring-orange-500 focus:ring-2"
              />
              <label htmlFor="content-rating-agreement" className="text-sm text-gray-300 leading-relaxed">
                I acknowledge that the assigned Content Rating of my series is{' '}
                <span className="text-orange-400 font-medium">
                  (Please complete the Self Assessment above to get the result)
                </span>.
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              disabled={submitting || !agreedToRating}
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold px-12 py-4 text-lg rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              {submitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5" />
                  <span>Create Series</span>
                </div>
              )}
            </Button>
          </div>
          
        </form>
      </div>
    </div>
  );
};

// Landing route: Use CANONICAL PageRenderer for homepage
const LandingRoute = () => {
  // Canonical path: CanonicalRouteProvider → registry lookup → PageRenderer
  // PageRenderer fetches from /api/page-builder/render/homepage
  return <PageRenderer pageId="homepage" />;
};

// Main App Component
function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);

  // Hide site chrome (nav, footer, back button) on immersive reader routes
  const isReaderRoute = location.pathname.startsWith('/read/');

  // Check if user should see onboarding modal
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.id && !hasCheckedOnboarding) {
        try {
          // Check if user has completed onboarding (localStorage first for speed)
          const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
          
          if (!hasCompletedOnboarding) {
            const token = localStorage.getItem('token');
            if (!token) {
              setHasCheckedOnboarding(true);
              return;
            }
            
            // Check onboarding status endpoint (fail-soft)
            const [onboardingRes, prefsRes] = await Promise.all([
              axios.get(`${API}/onboarding/status`, {
                headers: { 'Authorization': `Bearer ${token}` },
                timeout: 5000
              }).catch(() => ({ data: { completed: false } })),
              axios.get(`${API}/user/preferences`, {
                headers: { 'Authorization': `Bearer ${token}` },
                timeout: 5000
              }).catch(() => ({ data: { genre_preferences: {} } }))
            ]);
            
            const isOnboardingComplete = onboardingRes.data?.completed;
            const hasPreferences = 
              prefsRes.data?.genre_preferences?.comics?.length > 0 ||
              prefsRes.data?.genre_preferences?.art?.length > 0 ||
              prefsRes.data?.genre_preferences?.music?.length > 0;
            
            if (!isOnboardingComplete && !hasPreferences) {
              // Small delay to let the user see the app first
              setTimeout(() => setShowOnboardingModal(true), 1000);
            } else {
              // Mark as completed in localStorage
              localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
            }
          }
          
          setHasCheckedOnboarding(true);
        } catch (error) {
          // Fail soft - just mark as checked, don't show error
          console.warn('Onboarding check skipped:', error.message);
          setHasCheckedOnboarding(true);
        }
      }
    };

    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasCheckedOnboarding]);

  const handleOnboardingComplete = async (selectedGenres, selectedTypes) => {
    try {
      const token = localStorage.getItem('token');
      
      // Track onboarding completion
      analytics.trackOnboardingGenreSet(selectedGenres, selectedTypes);
      
      // Save preferences to database (use PUT for upsert)
      await Promise.all([
        axios.put(`${API}/user/preferences`, {
          genre_preferences: {
            comics: selectedGenres,
            art: selectedGenres,
            music: selectedGenres
          },
          content_type_preferences: selectedTypes
        }, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 10000
        }),
        // Mark onboarding as complete
        axios.post(`${API}/onboarding/complete`, {}, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 10000
        })
      ]);

      // Save to localStorage for fast access
      localStorage.setItem('feed_genres', JSON.stringify(selectedGenres));
      localStorage.setItem('feed_types', JSON.stringify(selectedTypes));
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');

      setShowOnboardingModal(false);
      toast.success('🎉 Your feed is now personalized!');
    } catch (error) {
      console.warn('Error saving onboarding preferences:', error.message);
      // Still mark as completed locally to avoid spam
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
      setShowOnboardingModal(false);
      toast.success('Preferences saved locally');
    }
  };

  const handleOnboardingSkip = () => {
    if (user) {
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    }
    setShowOnboardingModal(false);
    toast.info('You can set your preferences anytime from the Feed page');
  };

  const pollPaymentStatus = async (sessionId, type, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success(`${type === 'subscription' ? 'Subscription' : 'Tip'} successful! Thank you for your support.`);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }

    // Retry after delay
    setTimeout(() => {
      pollPaymentStatus(sessionId, type, attempts + 1);
    }, 3000);
  };

  // Check for payment completion
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const type = urlParams.get('type');
    
    if (sessionId) {
      pollPaymentStatus(sessionId, type);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className="App">
      {/* Onboarding Genre Modal */}
      <OnboardingGenreModal
        isOpen={showOnboardingModal}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
      
      <ChatPopupProvider>
          {!isReaderRoute && <AnnouncementBar />}
          {!isReaderRoute && <NewNavigation />}
          {!isReaderRoute && <GlobalBackButton />}
          <EarlyAccessGate isAuthenticated={!!user}>
          {/* CanonicalRouteProvider enforces Page Registry for public content pages */}
          <CanonicalRouteProvider>
          <Routes>
          {/* Early Access Gate Route - must be outside the gate check */}
          <Route path="/early-access" element={<EarlyAccessPage />} />
          <Route path="/" element={<LandingRoute />} />
          <Route path="/landing" element={<LandingRoute />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/search" element={<SearchResults />} />
          {/* /home redirects to canonical homepage - avoid duplicate page entries */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          {/* Browse pages rendered via PageRenderer through CanonicalRouteProvider */}
          <Route path="/comics" element={<ComicsPage />} />
          <Route path="/novels" element={<NovelsPage />} />
          {/* Music routes disabled for launch - will be re-enabled in future */}
          {/* <Route path="/music" element={<MusicPage />} /> */}
          <Route path="/live" element={<LivePage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/mature" element={<MaturePage />} />
          <Route path="/browse" element={<Navigate to="/comics" replace />} />
          <Route
            path="/dashboard"
            element={
              // Admin users go to admin dashboard, not creator/reader dashboard
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : user && (user.is_creator === true || user.role === 'creator')
                  ? <CreatorDashboard />
                  : <ReaderDashboard />
            }
          />
          <Route path="/series/:seriesId" element={USE_CANONICAL_SERIES_DETAIL ? <SeriesPageRenderer /> : <SeriesDetail />} />
          <Route path="/series/:seriesId/read/:chapterId" element={<EpisodeReader />} />
          
          {/* Canonical Reader Route - supports both comics and novels */}
          <Route path="/read/:contentType/:contentId/:unitId" element={<ReaderPageRenderer />} />
          
          {/* Creator-only routes - Admin users are blocked */}
          <Route path="/series/:seriesId/episode/:chapterId/pages" element={<EpisodePageManager />} />

          <Route 
            path="/series/:seriesId/upload" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <UploadEpisode />
            } 
          />
          {/* Reader Library route - for creators to access their reading list */}
          <Route 
            path="/reader-library" 
            element={<ReaderDashboard />} 
          />
          {/* Unified Profile Routes */}
          <Route path="/u/:handle" element={USE_CANONICAL_CREATOR_PROFILE ? <CreatorPageRenderer /> : <CreatorProfile />} />
          <Route path="/u-old/:handle" element={<UnifiedProfile />} />
          
          {/* Legacy redirects to unified profile */}
          <Route path="/creator/:handle" element={<CreatorRedirect />} />
          <Route path="/reader/:handle" element={<ReaderRedirect />} />
          <Route path="/user/:handle" element={<UserRedirect />} />
          <Route path="/@:handle" element={<AtHandleRedirect />} />
          
          {/* Profile Editor */}
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/studio/profile/edit" element={<EditProfile />} />
          <Route path="/profile/editor/:profileType" element={<ProfileEditor />} />
          <Route path="/profile/editor" element={<ProfileEditor />} />
          {/* Creator publishing routes - Admin users are blocked */}
          <Route 
            path="/publish" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <PublishHub />
            } 
          />
          <Route 
            path="/create-novel" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <CreateNovel />
            } 
          />
          <Route path="/novel/:novelId" element={USE_CANONICAL_NOVEL_DETAIL ? <NovelPageRenderer /> : <NovelDisabledFallback />} />
          <Route path="/novel/:novelId/add-chapter" element={<AddChapter />} />
          <Route path="/novel/:novelId/read/:chapterId" element={<NovelReader />} />

          <Route path="/novel/:novelId/chapter/:chapterId/edit" element={<AddChapter />} />
          <Route 
            path="/create-series" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <CreateSeries />
            } 
          />
          <Route 
            path="/upload-artwork" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <UploadArtwork />
            } 
          />
          <Route path="/art-gallery" element={<ArtGallery />} />
          <Route path="/gallery" element={<Navigate to="/art-gallery" replace />} />
          <Route path="/art/:postId" element={<ArtworkDetail />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route 
            path="/creator/analytics" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <CreatorAnalytics />
            } 
          />
          <Route 
            path="/bulk-upload" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <BulkUpload />
            } 
          />
          <Route path="/tip-success" element={<TipSuccess />} />
          <Route path="/u/:handle/follow" element={<FollowList />} />
          {/* Admin-only routes - Components handle their own auth/redirect */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/site-manager" element={<SiteManager />} />
          <Route path="/admin/users" element={<AdminUsersList />} />
          <Route path="/admin/creators" element={<AdminCreatorsList />} />
          <Route path="/admin/series" element={<AdminSeriesList />} />
          <Route path="/admin/artworks" element={<AdminArtworksList />} />
          <Route path="/admin/novels" element={<AdminNovelsList />} />
          <Route path="/admin/reports" element={<AdminReportsList />} />
          <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
          <Route path="/admin/audit/covers" element={<AdminCoverAudit />} />
          <Route path="/admin/notifications" element={<AdminNotifications />} />
          <Route path="/admin/metrics/activation" element={<AdminCreatorActivation />} />
          <Route path="/admin/metrics/onboarding" element={<AdminOnboardingMetrics />} />
          {/* Page Builder Routes - wrapped with DesignTokensProvider */}
          <Route path="/admin/page-builder" element={<DesignTokensProvider><PageBuilderIndexRenderer /></DesignTokensProvider>} />
          <Route path="/admin/page-builder/homepage" element={<DesignTokensProvider><PageBuilderStudio pageIdOverride="homepage" /></DesignTokensProvider>} />
          <Route path="/admin/page-builder/landing" element={<DesignTokensProvider><PageBuilderStudio pageIdOverride="landing_early_access" /></DesignTokensProvider>} />
          {/* Dynamic route for custom pages - must be AFTER specific routes */}
          <Route path="/admin/page-builder/:pageId" element={<DesignTokensProvider><PageBuilderStudio /></DesignTokensProvider>} />
          <Route path="/admin/homepage-health" element={<AdminHomepageHealth />} />
          <Route path="/admin/bug-reports" element={<AdminBugReports />} />
          <Route path="/admin/site-config" element={<AdminSiteConfig />} />
          <Route path="/admin/alert-settings" element={<AdminAlertSettings />} />
          {/* Publisher Admin Routes */}
          <Route path="/admin/publishers" element={<AdminPublishers />} />
          <Route path="/admin/publisher-associations" element={<AdminPublisherAssociations />} />
          <Route path="/admin/publisher-requests" element={<AdminPublisherRequests />} />
          {/* Admin Detail Pages */}
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/creators/:id" element={<AdminUserDetail />} />
          <Route path="/admin/series/:id" element={<AdminSeriesDetail />} />
          <Route path="/admin/artworks/:id" element={<AdminArtworkDetail />} />
          <Route path="/admin/novels/:id" element={<AdminNovelDetail />} />
          <Route path="/admin/reports/:id" element={<AdminReportDetail />} />
          {/* Public Publisher Routes */}
          <Route path="/publishers" element={<PublishersPage />} />
          <Route path="/publisher/:slug" element={<PublisherPage />} />
          <Route path="/verification" element={<VerificationSubmission />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/saved" element={<SavedLibrary />} />
          <Route path="/inbox" element={<UserInbox />} />
          <Route path="/rewards" element={<UserRewards />} />
          <Route 
            path="/collab-board" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <CollabBoard />
            } 
          />
          <Route 
            path="/collab/:requestId" 
            element={
              user && (user.role === 'admin' || user.is_admin === true)
                ? <Navigate to="/admin" replace />
                : <CollabRequestDetail />
            } 
          />
          <Route path="/human-verified" element={<HumanVerifiedLanding />} />
          <Route path="/email-testing" element={<EmailTesting />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/dmca" element={<DMCAPolicy />} />
          <Route path="/content-guidelines" element={<ContentGuidelines />} />
        </Routes>
        </CanonicalRouteProvider>
        </EarlyAccessGate>
        {!isReaderRoute && <Footer isAuthenticated={!!user} />}
        <CookieConsent />
        <OnboardingTutorial user={user} />
        <BugReportWidget user={user} />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1f2937',
              color: '#f3f4f6',
              border: '1px solid #374151',
            },
          }}
        />
        <ChatPopup />
      </ChatPopupProvider>
    </div>
  );
}

// Upload Episode Page: thin wrapper around UploadEpisodeForm
import UploadEpisodeForm from "./components/UploadEpisodeForm";

const UploadEpisode = () => {
  const { seriesId } = useParams();
  const [searchParams] = useSearchParams();
  const episodeId = searchParams.get('episodeId');
  const [seriesTitle, setSeriesTitle] = useState("");

  useEffect(() => {
    const fetchSeriesTitle = async () => {
      try {
        const response = await axios.get(`${API}/series/${seriesId}`);
        setSeriesTitle(response.data?.title || "");
      } catch (error) {
        console.error("Error fetching series:", error);
      }
    };

    if (seriesId) {
      fetchSeriesTitle();
    }
  }, [seriesId]);

  return <UploadEpisodeForm seriesId={seriesId} seriesTitle={seriesTitle} episodeId={episodeId} />;
};

// Main App Component with Auth Provider
function App() {
  return (
    <AuthProvider>
      <SiteConfigProvider>
        <AppContent />
      </SiteConfigProvider>
    </AuthProvider>
  );
}

export default App;